import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories, trendingItems, testimonials, brands, collections } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrgJsonLd from '@/components/feature/SchemaOrgJsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.space';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png';

const homeSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Superbuy Spreadsheet Hub',
    url: SITE_URL,
    description: 'Curated shopping spreadsheets, real QC photos, brand guides, and expert buying tips for international shoppers.',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Superbuy Spreadsheet Hub',
    url: SITE_URL,
    logo: LOGO_URL,
    description: 'The premier resource for curated Superbuy spreadsheets featuring verified product links, real QC photos, brand guides, and expert buying tips across 14 categories and 80+ brands.',
    sameAs: [
      'https://kaspreadsheet.com',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is a Superbuy Spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A Superbuy Spreadsheet is a curated collection of verified product links organized by category and brand. It helps international shoppers find quality products from platforms like Taobao, Weidian, and 1688 without spending hours searching manually. Each listing includes QC photos, seller ratings, and price estimates.',
        },
      },
      {
        '@type': 'Question',
        name: 'How often is the spreadsheet updated?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The spreadsheet is updated daily with new finds, removed dead links, and refreshed QC photos. Our community of experienced buyers continuously contributes new discoveries across all 14 categories and 80+ brands to keep the content fresh and reliable.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are all the links in the spreadsheet verified?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Every link in the spreadsheet has been checked by experienced buyers. We include real QC photos, seller ratings, and community feedback to ensure you can shop with confidence. Links that receive negative community feedback are flagged and removed within 24 hours.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I buy items from the spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Click any link in the spreadsheet to open the product page. Copy the link into an agent like Superbuy, purchase the item, review QC photos at the warehouse, and then ship internationally.',
        },
      },
      {
        '@type': 'Question',
        name: 'Which buying agent works best with the spreadsheet?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Superbuy is the most popular choice for spreadsheet users because of its direct link import feature and free QC photos. AllChinaBuy and CSSBuy are solid alternatives.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is the Superbuy Spreadsheet free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. The spreadsheet is freely accessible to all users. Simply browse the categories, find what you like, and click through to start shopping. No subscriptions, no hidden fees.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I request specific brands or items to be added?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. The spreadsheet community welcomes requests. If there is a brand, item, or category you would like to see added, reach out through the main site and our curators will prioritize your request in the next update cycle.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does international shipping work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'After you approve QC photos, the agent ships your items using your chosen carrier. Options include budget sea freight (3-5 weeks) and express air courier (5-10 days). Shipping costs depend on weight, volume, and destination.',
        },
      },
    ],
  },
];

export default function Home() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  return (
    <>
      {homeSchemas.map((schema, idx) => (
        <SchemaOrgJsonLd key={idx} schema={schema} />
      ))}
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[620px] md:min-h-[750px] flex items-center justify-center overflow-hidden pt-16">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Aerial%20flatlay%20photography%20with%20an%20artistic%20grid%20layout%20of%20assorted%20fashion%20accessories%20shoes%20bags%20and%20clothing%20items%20arranged%20in%20neat%20rows%20on%20warm%20terracotta%20and%20sand%20colored%20paper%20background%2C%20natural%20overhead%20lighting%20casting%20gentle%20shadows%2C%20organized%20editorial%20catalog%20aesthetic%20with%20muted%20earth%20tones%20and%20occasional%20sage%20green%20accents&width=1800&height=900&seq=hero-bg-v2-superbuy&orientation=landscape"
              alt="Superbuy Spreadsheet Hub — curated shopping resources and guides"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/70"></div>
          </div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-background-200/70 bg-background-50/80 backdrop-blur-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
              <span className="text-xs font-medium text-foreground-600">Updated Daily — 14 Curated Categories</span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-tight mb-4">
              Your Ultimate Buying{' '}
              <span className="relative inline-block">
                Spreadsheet
                <span className="absolute -bottom-1 left-0 w-full h-2 md:h-3 bg-accent-200/60 -z-10 rounded-full"></span>
              </span>
            </h1>

            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Curated spreadsheets and real buying guides for international shoppers.
              Stop guessing, start saving with verified links, QC photos, and expert picks.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-5">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 border-2 border-foreground-200 text-foreground-800 font-semibold text-sm rounded-full hover:border-accent-400 hover:text-accent-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-bag-line ml-2"></i>
              </a>
            </div>

            <div className="mt-12 flex items-center justify-center gap-8">
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">10K+</p>
                <p className="text-xs text-foreground-500 mt-1">Active Shoppers</p>
              </div>
              <div className="w-px h-10 bg-background-300"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">14</p>
                <p className="text-xs text-foreground-500 mt-1">Categories</p>
              </div>
              <div className="w-px h-10 bg-background-300"></div>
              <div className="text-center">
                <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">80+</p>
                <p className="text-xs text-foreground-500 mt-1">Brands Covered</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Grid Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full border border-background-200 text-xs font-medium text-foreground-500 mb-4">
                14 Curated Categories
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                Shop by Category
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                Browse our curated collections — every category links directly to the latest spreadsheet finds
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-product-shop>
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/60 hover:bg-accent-50 hover:border-accent-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-background-200/70 flex items-center justify-center mb-3 group-hover:bg-accent-100 transition-colors">
                    <i className={`${cat.icon} text-lg text-foreground-600 group-hover:text-accent-600 transition-colors`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1 group-hover:text-accent-700 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {cat.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Brands Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full border border-background-200 text-xs font-medium text-foreground-500 mb-4">
                12 Featured Brands
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                Top Brand Spreadsheets
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                Every major brand has a dedicated spreadsheet with verified links, QC photos, and community ratings — updated daily
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {brands.map((brand) => (
                <a
                  key={brand.slug}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden hover:border-accent-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-40 md:h-48 overflow-hidden">
                    <img
                      src={brand.image}
                      alt={brand.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-50/60 to-transparent"></div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1.5 group-hover:text-accent-700 transition-colors">
                      {brand.name}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                      {brand.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-foreground-500 mb-3">
                And 70+ more brands including Moncler, Dior, LV, Prada, Rolex, Apple, Sony, and many others
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 border border-foreground-200 text-foreground-700 font-medium text-sm rounded-full hover:border-accent-300 hover:text-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Browse All 80+ Brand Spreadsheets
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Collections Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full border border-background-200 text-xs font-medium text-foreground-500 mb-4">
                Handpicked Collections
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                Curated Collections
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                Themed spreadsheet collections — best sneakers, seasonal picks, budget finds, and editor favorites all in one place
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
              {collections.map((col) => (
                <a
                  key={col.slug}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden hover:border-accent-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-44 md:h-52 overflow-hidden">
                    <img
                      src={col.image}
                      alt={col.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-0.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1.5 group-hover:text-accent-700 transition-colors">
                      {col.name}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                      {col.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="rounded-2xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Dynamic%20gradient%20composition%20of%20overlapping%20translucent%20product%20cards%20in%20warm%20coral%20pink%20peach%20and%20soft%20mint%20green%20tones%20floating%20on%20bright%20white%20background%2C%20modern%20editorial%20layout%20design%20with%20varied%20opacity%20layers%20and%20subtle%20drop%20shadows%2C%20clean%20ecommerce%20catalog%20aesthetic&width=1600&height=500&seq=cta-visual-v2&orientation=landscape"
                alt="Curated product finds — Access the Superbuy Spreadsheet for the best deals"
                className="w-full h-[250px] md:h-[350px] object-cover"
              />
            </div>

            <div className="text-center mb-10">
              <p className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-snug mb-2">
                Stop <span className="italic font-light text-foreground-400">overpaying</span>. Start{' '}
                <span className="relative inline-block">
                  spreadsheeting
                  <span className="absolute -bottom-0.5 left-0 w-full h-1.5 bg-secondary-200/60 -z-10 rounded-full"></span>
                </span>.
              </p>
            </div>

            <div className="text-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Visit Main Store
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* SEO Content / About Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full border border-background-200 text-xs font-medium text-foreground-500 mb-5">
                  Editor&apos;s Pick
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-6">
                  Why Smart Buyers Trust Superbuy Spreadsheet Hubs in 2025
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  The Superbuy Spreadsheet ecosystem has grown into the most reliable resource for international shoppers. With curated links updated daily, real QC photos verified by the community, and a structured approach to product discovery, spreadsheets eliminate the guesswork from overseas shopping. Over 10,000 active users rely on these spreadsheets every month to find quality products at fair prices without spending hours scrolling through unverified listings.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  What makes a Superbuy Spreadsheet different from a random link list is its meticulous curation. Every item goes through multiple layers of verification: the seller&apos;s reputation is checked, product photos are compared against real QC submissions, and community feedback is incorporated before a link earns its spot. This means you are not just browsing — you are accessing a filtered, organized, and continuously maintained shopping resource.
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                  Whether you are hunting for the latest sneaker releases, premium hoodies, or hard-to-find accessories, a well-maintained Superbuy Spreadsheet gives you direct access to thousands of vetted product links. The spreadsheet format makes it easy to compare prices, check quality ratings, and track your hauls — all in one place. Plus, with the{' '}
                  <Link to="/guide/superbuy-spreadsheet-101" className="text-accent-600 hover:text-accent-700 underline font-medium">
                    beginner&apos;s guide
                  </Link>
                  {' '}available on this site, even first-time international shoppers can get started in minutes.
                </p>

                <div className="bg-background-100 rounded-xl border border-background-200/60 p-5 md:p-6 mb-8">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                    Superbuy Spreadsheet 101: How to Find Hidden Gems in 2025
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    Learn how to navigate spreadsheets like a pro — from filtering by brand and category to reading QC photos and comparing seller ratings. This comprehensive guide covers everything a beginner needs to start finding quality products at unbeatable prices across 14 categories and 80+ brands.
                  </p>
                  <Link
                    to="/guide/superbuy-spreadsheet-101"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-accent-300 text-accent-700 font-medium text-sm rounded-full hover:bg-accent-50 transition-colors whitespace-nowrap"
                  >
                    Read Full Guide
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>

              <div className="lg:w-[380px] flex-shrink-0">
                <div className="bg-accent-600 rounded-2xl p-6 md:p-8 text-background-50">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-medium text-accent-100">Trusted by 10K+ Shoppers</span>
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-300"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-300"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-300"></span>
                    </div>
                  </div>
                  <p className="text-lg md:text-xl leading-relaxed mb-6">
                    &ldquo;This hub completely changed how I shop from overseas. The curated spreadsheets alone save me hours every week.&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center text-sm font-bold">
                      JK
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Jordan K.</p>
                      <p className="text-xs text-accent-200">Fashion Reseller</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trending Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">Trending Now</h2>
                <p className="text-sm text-foreground-500 mt-1">Hot items from the latest spreadsheet updates</p>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                View All Finds
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {trendingItems.map((item, idx) => (
                <a
                  key={idx}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group rounded-xl bg-background-50 border border-background-200/60 overflow-hidden hover:border-accent-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-square bg-background-200/50 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {item.hot && (
                      <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold">
                        Hot Drop
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-medium text-sm text-foreground-900 mb-1 line-clamp-2">{item.name}</p>
                    <p className="text-sm font-semibold text-accent-700">{item.price}</p>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-6 text-center sm:hidden">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                View All Finds
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="bg-foreground-900 rounded-2xl overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-[40%] relative">
                  <div className="aspect-[3/4] md:aspect-auto md:h-full relative overflow-hidden">
                    <img
                      src={testimonials[currentTestimonial].image}
                      alt={testimonials[currentTestimonial].name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-foreground-900/20"></div>
                  </div>
                </div>

                <div className="md:w-[60%] p-8 md:p-12 flex flex-col justify-center">
                  <span className="text-5xl md:text-6xl font-serif text-accent-300 leading-none mb-2">&ldquo;</span>
                  <p className="text-lg md:text-xl font-medium text-background-50 leading-relaxed mb-6">
                    {testimonials[currentTestimonial].text}
                  </p>
                  <div className="mb-8">
                    <p className="font-heading font-bold text-background-50">{testimonials[currentTestimonial].name}</p>
                    <p className="text-sm text-foreground-400">{testimonials[currentTestimonial].role}</p>
                  </div>

                  <div className="flex items-center gap-3">
                    {testimonials.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentTestimonial(idx)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                          idx === currentTestimonial
                            ? 'bg-accent-400 w-7'
                            : 'bg-foreground-600 hover:bg-foreground-500'
                        }`}
                        aria-label={`View testimonial ${idx + 1}`}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Extended Content Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <article>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                Everything You Need to Know About Superbuy Spreadsheets
              </h2>

              <div className="space-y-6 text-foreground-700 leading-relaxed">
                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    What Is a Superbuy Spreadsheet?
                  </h3>
                  <p className="text-sm md:text-base">
                    A Superbuy Spreadsheet is a meticulously curated document that compiles thousands of verified product links from platforms like Taobao, Weidian, and 1688. Created by experienced buyers and continuously updated, these spreadsheets categorize items by brand, type, price range, and quality rating. Instead of spending hours searching through countless listings and risking scams, shoppers can open a spreadsheet and instantly access pre-vetted finds with real QC (Quality Check) photos, seller ratings, and direct purchase links. The spreadsheet format makes it uniquely efficient — you can filter, sort, and compare hundreds of items in the time it would take to manually browse a handful of product pages.
                  </p>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    Why Use a Spreadsheet Instead of Searching Manually?
                  </h3>
                  <p className="text-sm md:text-base mb-3">
                    Manual searching on platforms like Taobao and Weidian can be overwhelming — the sheer volume of products, language barriers, and inconsistent quality make it hard to find genuine bargains. A Superbuy Spreadsheet solves these problems in several key ways:
                  </p>
                  <ul className="space-y-2 pl-5 list-disc text-sm md:text-base">
                    <li><strong className="text-foreground-900">Pre-vetted listings</strong> — every link has been checked by experienced buyers, reducing the risk of receiving low-quality or incorrect items.</li>
                    <li><strong className="text-foreground-900">Real QC photos</strong> — see actual photos of products taken by other buyers before you commit to a purchase.</li>
                    <li><strong className="text-foreground-900">Organized categories</strong> — find exactly what you are looking for, from shoes and hoodies to bags and accessories, all neatly sorted.</li>
                    <li><strong className="text-foreground-900">Price transparency</strong> — compare prices across multiple sellers and avoid overpaying for the same item.</li>
                    <li><strong className="text-foreground-900">Community-driven updates</strong> — spreadsheets are updated regularly based on community feedback and new finds.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    How to Get Started with Superbuy Spreadsheet
                  </h3>
                  <p className="text-sm md:text-base mb-3">
                    Getting started with a Superbuy Spreadsheet is straightforward. First, visit the main spreadsheet hub where you can browse categories and find items that match your interests. Each product listing includes the brand name, item description, price range, and a direct link to the selling platform. For beginners, we recommend starting with popular categories like Shoes, Hoodies, and Accessories to get familiar with the spreadsheet format and the buying process.
                  </p>
                  <p className="text-sm md:text-base">
                    Once you have found an item you like, simply click the link to open the product page on the selling platform. From there, you can use Superbuy or any other trusted agent to purchase, inspect, and ship your items internationally. Want a deeper dive? Check out our{' '}
                    <Link to="/guide/superbuy-spreadsheet-101" className="text-accent-600 hover:text-accent-700 underline font-medium">
                      complete beginner&apos;s guide
                    </Link>
                    {' '}for step-by-step instructions with screenshots.
                  </p>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    The Categories That Matter Most
                  </h3>
                  <p className="text-sm md:text-base">
                    Our spreadsheet covers 14 essential categories to ensure you never miss a great find. Shoes and Sneakers remain the most popular category, featuring everything from budget-friendly daily wear to rare limited editions. Hoodies and Sweaters offer cozy options from premium streetwear brands. T-Shirts, Jackets, and Pants provide the foundation for any wardrobe. Niche categories like Headwear, Jersey, Perfumes, and Belts help you accessorize with confidence. And our &ldquo;Other Stuff&rdquo; category captures all the hidden gems that do not fit neatly elsewhere — from electronics to home goods.
                  </p>
                </section>
              </div>
            </article>
          </div>
        </section>

        {/* Comparison Table Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Spreadsheet vs. Manual Searching: A Comparison
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                See why thousands of shoppers have switched to spreadsheet-based buying
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-4 font-semibold text-foreground-900">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-accent-700">
                      <i className="ri-database-2-line mr-1.5"></i>Superbuy Spreadsheet
                    </th>
                    <th className="text-left px-5 py-4 font-semibold text-foreground-600">Manual Searching</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  {[
                    { feature: 'Product Discovery Speed', sheet: 'Instant — filtered lists ready to browse', manual: 'Slow — endless scrolling and search queries' },
                    { feature: 'Quality Assurance', sheet: 'QC photos and community ratings included', manual: 'None — rely on seller photos only' },
                    { feature: 'Price Comparison', sheet: 'Side-by-side across multiple sellers', manual: 'Manual tab switching required' },
                    { feature: 'Dead Link Handling', sheet: 'Continuously pruned and updated', manual: 'No mechanism — wasted time on dead links' },
                    { feature: 'Language Barrier', sheet: 'English descriptions and notes provided', manual: 'Translation tools needed per listing' },
                    { feature: 'Scam Protection', sheet: 'Community-vetted; flagged scams removed', manual: 'High risk — no vetting mechanism' },
                    { feature: 'Category Organization', sheet: '14 structured categories with sub-filters', manual: 'Platform search may return mixed results' },
                    { feature: 'Update Frequency', sheet: 'Daily updates with new finds', manual: 'You search from scratch every time' },
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                      <td className="px-5 py-3.5 font-medium text-foreground-800">{row.feature}</td>
                      <td className="px-5 py-3.5 text-foreground-600">{row.sheet}</td>
                      <td className="px-5 py-3.5 text-foreground-500">{row.manual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Agent Comparison Table Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Buying Agent Comparison
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                Once you find items in the spreadsheet, choose the right agent to purchase and ship them
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-background-200/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-50">
                    <th className="text-left px-5 py-4 font-semibold text-foreground-900">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-accent-700">Superbuy</th>
                    <th className="text-left px-5 py-4 font-semibold text-foreground-600">AllChinaBuy</th>
                    <th className="text-left px-5 py-4 font-semibold text-foreground-600">CSSBuy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/60">
                  {[
                    { feature: 'QC Photos', sb: 'Included — 5 free', acb: 'Included — 3 free', cs: 'Available — paid' },
                    { feature: 'Warehouse Storage', sb: '90 days free', acb: '90 days free', cs: '30 days free' },
                    { feature: 'Shipping Options', sb: '12+ carriers', acb: '8+ carriers', cs: '6+ carriers' },
                    { feature: 'User Interface', sb: 'Clean, modern dashboard', acb: 'Functional, improving', cs: 'Basic, utilitarian' },
                    { feature: 'Customer Support', sb: '24/7 live chat', acb: 'Email + ticket system', cs: 'Ticket system' },
                    { feature: 'Platform Coverage', sb: 'Taobao, Weidian, 1688', acb: 'Taobao, Weidian, 1688', cs: 'Taobao, Weidian' },
                    { feature: 'Mobile App', sb: 'iOS + Android', acb: 'Web only', cs: 'Android only' },
                    { feature: 'Spreadsheet Compatibility', sb: 'Direct link import', acb: 'Manual paste', cs: 'Manual paste' },
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}>
                      <td className="px-5 py-3.5 font-medium text-foreground-800">{row.feature}</td>
                      <td className="px-5 py-3.5 text-foreground-600">{row.sb}</td>
                      <td className="px-5 py-3.5 text-foreground-600">{row.acb}</td>
                      <td className="px-5 py-3.5 text-foreground-600">{row.cs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-foreground-500 mb-3">
                For the best spreadsheet-to-purchase workflow, Superbuy remains the top recommendation among our community.{' '}
                <Link to="/guide/superbuy-spreadsheet-101" className="text-accent-600 hover:text-accent-700 underline font-medium">
                  Learn more in our shipping guide
                </Link>
              </p>
            </div>
          </div>
        </section>

        {/* Extended SEO - Buying Process */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <article>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                The Complete Buying Workflow: From Spreadsheet to Doorstep
              </h2>
              <div className="space-y-6 text-foreground-700 leading-relaxed">
                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    Step 1: Browse the Spreadsheet and Pick Your Items
                  </h3>
                  <p className="text-sm md:text-base">
                    Start by opening the Superbuy Spreadsheet and navigating to your preferred category — whether that is{' '}
                    <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 underline font-medium">Shoes</a>,{' '}
                    <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 underline font-medium">Hoodies</a>, or{' '}
                    <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 underline font-medium">Accessories</a>. Each listing includes the product name, brand, price estimate, size availability, and a direct link to the selling platform. Review the QC photos attached to each listing — these are real photographs taken by community members who have already purchased the item, giving you a true representation of what you will receive.
                  </p>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    Step 2: Purchase Through a Trusted Agent
                  </h3>
                  <p className="text-sm md:text-base mb-3">
                    Once you have selected your items, copy the product links and paste them into your chosen agent&apos;s platform. Superbuy is the most popular choice because of its spreadsheet-friendly workflow — it can import links in bulk and automatically generate purchase orders. Other agents like AllChinaBuy and CSSBuy also work, though they may require manual entry for each link. The agent will purchase the items on your behalf from the Chinese platform and have them delivered to their warehouse.
                  </p>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    Step 3: Review QC Photos
                  </h3>
                  <p className="text-sm md:text-base mb-3">
                    After the items arrive at the warehouse, the agent takes detailed QC photos — typically 3-5 shots showing the front, back, tags, logos, and any important details. This is your opportunity to verify that the item matches the listing description. If something looks off — wrong size, color mismatch, or quality issues — you can request a return or exchange before international shipping. This QC step is one of the biggest advantages of using a spreadsheet-based workflow, as the community reviews help you know what to look for.
                  </p>
                </section>

                <section>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                    Step 4: Choose Shipping and Track Your Haul
                  </h3>
                  <p className="text-sm md:text-base">
                    Once you approve the QC photos, select an international shipping method. Options range from budget-friendly sea freight (3-5 weeks) to express air courier services (5-10 days). The spreadsheet community frequently shares shipping tips and carrier recommendations in our{' '}
                    <Link to="/guide/superbuy-spreadsheet-101" className="text-accent-600 hover:text-accent-700 underline font-medium">
                      beginner&apos;s guide
                    </Link>. After shipping, track your parcel through the agent&apos;s dashboard until it arrives at your doorstep — and then share your own QC photos to help the next spreadsheet user make an informed decision.
                  </p>
                </section>
              </div>
            </article>
          </div>
        </section>

        {/* Extended SEO - Pro Tips */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <article>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                10 Pro Tips for Superbuy Spreadsheet Users
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {[
                  { num: '01', title: 'Always Check QC Before Shipping', text: 'Never skip the warehouse QC photo review. Community reports show that roughly 15% of items have minor discrepancies — catching them at the warehouse saves international return shipping costs.' },
                  { num: '02', title: 'Sort by Rating, Not Just Price', text: 'The cheapest listing is not always the best deal. Our spreadsheet includes community quality ratings — prioritize items rated 4 stars and above for the best experience.' },
                  { num: '03', title: 'Bundle Items to Save on Shipping', text: 'Combine multiple items into one parcel. Most agents offer free warehouse storage for up to 90 days, so you can accumulate items before shipping.' },
                  { num: '04', title: 'Use the Measurement Guides', text: 'Asian sizing runs smaller than Western sizing. Always check the measurement charts in the spreadsheet before ordering — especially for shoes and fitted clothing.' },
                  { num: '05', title: 'Read Seller Reviews in the Spreadsheet', text: 'Each listing includes aggregated seller feedback. Sellers with consistent 5-star ratings across 100+ transactions are generally the safest bets.' },
                  { num: '06', title: 'Check Dead Link Status Before Planning', text: 'Spreadsheet links are updated daily, but high-demand items sell out fast. Items marked with a green status badge have been confirmed active within the last 48 hours.' },
                  { num: '07', title: 'Join the Community for Insider Tips', text: 'The spreadsheet community shares finds, QC comparisons, and shipping hacks that are not publicly listed. Active members often get early access to new spreadsheet drops.' },
                  { num: '08', title: 'Use Multiple Agents for Large Hauls', text: 'For hauls over 10kg, splitting your order between two agents can reduce customs scrutiny and sometimes lower shipping costs per kilogram.' },
                  { num: '09', title: 'Leverage Seasonal Spreadsheet Drops', text: 'Special edition spreadsheets are released during major shopping seasons — 11.11 Singles Day, Chinese New Year, and mid-year sales feature exclusive discounts.' },
                  { num: '10', title: 'Contribute Your Own QC Photos', text: 'The spreadsheet ecosystem thrives on community contributions. Upload your QC photos after receiving items — you help future buyers and earn contributor badges.' },
                ].map((tip) => (
                  <div key={tip.num} className="bg-background-50 rounded-xl border border-background-200/60 p-5">
                    <span className="text-3xl font-heading font-bold text-foreground-200 mb-2 block">{tip.num}</span>
                    <h4 className="font-heading font-semibold text-sm text-foreground-900 mb-1.5">{tip.title}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{tip.text}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                Frequently Asked Questions
              </h2>
              <p className="text-sm text-foreground-500">Quick answers to common questions about Superbuy Spreadsheets</p>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: 'What is a Superbuy Spreadsheet?',
                  a: 'A Superbuy Spreadsheet is a curated collection of verified product links organized by category and brand. It helps international shoppers find quality products from platforms like Taobao, Weidian, and 1688 without spending hours searching manually. Each listing includes QC photos, seller ratings, and price estimates.'
                },
                {
                  q: 'How often is the spreadsheet updated?',
                  a: 'The spreadsheet is updated daily with new finds, removed dead links, and refreshed QC photos. Our community of experienced buyers continuously contributes new discoveries across all 14 categories and 80+ brands to keep the content fresh and reliable.'
                },
                {
                  q: 'Are all the links in the spreadsheet verified?',
                  a: 'Yes. Every link in the spreadsheet has been checked by experienced buyers. We include real QC photos, seller ratings, and community feedback to ensure you can shop with confidence. Links that receive negative community feedback are flagged and removed within 24 hours.'
                },
                {
                  q: 'How do I buy items from the spreadsheet?',
                  a: 'Click any link in the spreadsheet to open the product page. Copy the link into an agent like Superbuy, purchase the item, review QC photos at the warehouse, and then ship internationally. Our beginner\'s guide walks you through every step with detailed instructions.'
                },
                {
                  q: 'Which buying agent works best with the spreadsheet?',
                  a: 'Superbuy is the most popular choice for spreadsheet users because of its direct link import feature and free QC photos. AllChinaBuy and CSSBuy are solid alternatives — check our comparison table above for a detailed side-by-side breakdown.'
                },
                {
                  q: 'Is the Superbuy Spreadsheet free to use?',
                  a: 'Yes. The spreadsheet is freely accessible to all users. Simply browse the categories, find what you like, and click through to start shopping. No subscriptions, no hidden fees — just curated finds at your fingertips.'
                },
                {
                  q: 'Can I request specific brands or items to be added?',
                  a: 'Absolutely. The spreadsheet community welcomes requests. If there is a brand, item, or category you would like to see added, reach out through the main site and our curators will prioritize your request in the next update cycle.'
                },
                {
                  q: 'How does international shipping work?',
                  a: 'After you approve QC photos, the agent ships your items using your chosen carrier. Options include budget sea freight (3-5 weeks) and express air courier (5-10 days). Shipping costs depend on weight, volume, and destination. See our guide for detailed shipping tips.'
                },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 md:py-5 cursor-pointer list-none">
                    <span className="font-medium text-sm md:text-base text-foreground-900 pr-4">{faq.q}</span>
                    <span className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 group-open:bg-accent-100 group-open:text-accent-600 transition-colors">
                      <i className="ri-add-line text-sm group-open:rotate-45 transition-transform"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-20 bg-accent-500">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
              Ready to Find Your Next Haul?
            </h2>
            <p className="text-sm md:text-base text-accent-100 mb-8 max-w-xl mx-auto">
              Access the complete Superbuy Spreadsheet with 14 categories, 80+ brands, thousands of verified product links, real QC photos, and community ratings — all updated daily.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-8 py-3.5 bg-background-50 text-accent-700 font-semibold text-sm rounded-full hover:bg-background-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
              <Link
                to="/guide/superbuy-spreadsheet-101"
                className="w-full sm:w-auto px-8 py-3.5 border-2 border-background-50/40 text-background-50 font-semibold text-sm rounded-full hover:bg-background-50/10 transition-all whitespace-nowrap"
              >
                Read the Guide
                <i className="ri-book-open-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}